import React, { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withStyles } from '@glu/theming';
import classnames from 'classnames';
import { DraggableModal } from '@ces/draggable-modal';
import Button from 'common/Button';
import dialogActions from 'core/dialogs/actions';
import dataActions from 'core/data/actions';
import Link from 'common/Link';
import Grid from './Grid';

const styles = ({ screenSizes, typography }) => ({
  button: {
    '&.btn-mobile': {
      background: 'none',
    },
    border: 0,
    fontSize: 16,
    lineHeight: '19px',
    marginTop: 15,
    padding: 0,
    textAlign: 'center',
    width: '100%',
    [`@media(min-width: ${screenSizes.mobileMax}px)`]: {
      '&.gridButton-mobile': {
        display: 'none',
      },
    },
    [`@media(max-width: ${screenSizes.mobileMax}px)`]: {
      '&.gridButton': {
        display: 'none',
      },
    },
  },
  dialogBody: {
    maxHeight: 444,
    overflowY: 'auto',
  },
  dialogHeader: {
    '& h1': {
      fontFamily: 'Arial',
      fontSize: 20,
      lineHeight: '23px',
      marginTop: 14,
      textTransform: 'uppercase',
    },
    '& svg': {
      marginTop: 18,
    },
    margin: [0, 'auto'],
  },
  dialogRoot: {
    margin: [20, 35],
  },
  rate: {
    '& h4': {
      ...typography.h4,
      lineHeight: '43px',
      textAlign: 'center',
    },
    '& p': {
      ...typography.p3,
      fontSize: 12,
      lineHeight: '14px',
      margin: 0,
      textAlign: 'center',
    },
    '&.withPadding': {
      '& p': {
        paddingRight: 28,
      },
    },
    '&:first-child': {
      marginRight: 37,
    },
  },
  root: {
    alignItem: 'center',
    display: 'flex',
    justifyContent: 'center',
  },
});

const OutputRates = ({
  amountValue,
  aprLabel,
  apyLabel,
  classes,
  fieldName,
  fieldRates,
  gridLabel,
  gridValue,
  hasTerm,
  help,
  onChange,
  openDialog,
  rateTierUrl,
  termValue,
}) => {
  const ratesTiers = useMemo(() => {
    if (!gridValue) {
      return {};
    }
    const { columns, rows } = gridValue;
    const termCol = columns.find((c) => c.type === 'term');
    const rangeCol = columns.find((c) => c.type === 'range');
    const aprCol = columns.find((c) => c.type === 'apr');
    const apyCol = columns.find((c) => c.type === 'apy');
    const term = termCol && termCol.name;
    const range = rangeCol && rangeCol.name;
    const apr = aprCol && aprCol.name;
    const apy = apyCol && apyCol.name;

    return rows.reduce((rates, row) => {
      const ratesKey = hasTerm ? row[term] : 'demand';
      let min;
      let max;
      if (range && row[range]) {
        [min, max] = row[range]
          .split('-')
          .map((s) => s.replace(/[$,]/g, ''))
          .map(parseFloat);
      }
      return {
        ...rates,
        [ratesKey]: [...(rates[ratesKey] || []), { apr: row[apr], apy: row[apy], max, min }],
      };
    }, {});
  }, [gridValue, hasTerm]);

  const { apr, apy } = useMemo(() => {
    const defaultVal = { apr: '', apy: '' };
    const tVal = hasTerm ? termValue : 'demand';
    if (!tVal || !ratesTiers[tVal]) {
      return defaultVal;
    }

    if (ratesTiers[tVal]) {
      return (
        ratesTiers[tVal].find((r) => r.min <= amountValue && r.max >= amountValue) ||
        ratesTiers[tVal].find((f) => !f.max && !f.min) ||
        defaultVal
      );
    }

    return defaultVal;
  }, [amountValue, hasTerm, ratesTiers, termValue]);

  useEffect(() => {
    onChange(fieldName, { apr, apy });
  }, [apr, apy, fieldName, onChange]);

  const [modalOpen, setModalOpen] = useState(false);
  const [y, setY] = useState(0);

  useEffect(() => {
    setModalOpen(y > 0);
  }, [y]);

  const hideTierAndFundingDetails = !gridValue.rows.some((row) => row.apr || row.apy);

  if (hideTierAndFundingDetails) return <></>;
  return (
    <>
      <div className={classnames('outputRates', classes.root)}>
        {apr && (
          <div className={classnames('rate', 'apr', classes.rate, { withPadding: !!apr })}>
            <p>{aprLabel}</p>
            <h4>{apr}</h4>
          </div>
        )}
        {apy && (
          <div className={classnames('rate', 'apy', classes.rate, { withPadding: !!apy })}>
            <p>{apyLabel}</p>
            <h4>{apy}</h4>
          </div>
        )}
      </div>
      <div>
        {rateTierUrl ? (
          <div className={classnames(classes.button)}>
            <Link variant="secondary" target="_blank" href={rateTierUrl}>
              {help}
            </Link>
          </div>
        ) : (
          <>
            <Button
              variant="secondary"
              className={classnames('btn', 'btn-mobile', 'gridButton', classes.button)}
              onClick={(e) => {
                const { clientY } = e;
                setY(() => (clientY > 300 ? clientY - 300 : 0));
                setModalOpen(true);
              }}
            >
              {help}
            </Button>
            <Button
              variant="secondary"
              className={classnames('btn', 'btn-mobile', 'gridButton-mobile', classes.button)}
              onClick={() => {
                openDialog('rates', { fieldRates, gridLabel });
              }}
            >
              {help}
            </Button>
            <DraggableModal
              isOpen={modalOpen}
              initialPosition={{ x: '60%', y }}
              minWidth={444}
              maxWidth={444}
              closeModal={() => setModalOpen(false)}
              title={gridLabel}
              classes={{
                dialogBody: classes.dialogBody,
                dialogRoot: classes.dialogRoot,
                header: classes.dialogHeader,
              }}
            >
              <Grid name={fieldRates} />
            </DraggableModal>
          </>
        )}
      </div>
    </>
  );
};
OutputRates.propTypes = {
  amountValue: PropTypes.number,
  aprLabel: PropTypes.string.isRequired,
  apyLabel: PropTypes.string.isRequired,
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  fieldName: PropTypes.string.isRequired,
  fieldRates: PropTypes.string,
  gridLabel: PropTypes.string,
  gridValue: PropTypes.shape({
    columns: PropTypes.arrayOf(PropTypes.shape({})),
    rows: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  hasTerm: PropTypes.bool.isRequired,
  help: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  openDialog: PropTypes.func.isRequired,
  rateTierUrl: PropTypes.string,
  termValue: PropTypes.string,
};

OutputRates.defaultProps = {
  amountValue: 0,
  fieldRates: null,
  gridLabel: '',
  gridValue: null,
  rateTierUrl: undefined,
  termValue: '',
};

const OutputRatesStyled = withStyles(styles)(OutputRates);

const mapStateToProps = ({ data, fields, resources }, { name }) => {
  const field = fields[name];
  const { aprLabel, apyLabel, fieldAmount, fieldRates, fieldTerm, help } = field;
  const { label } = fields[fieldRates] || {};
  const amountValue =
    typeof data[fieldAmount] === 'string'
      ? parseFloat(data[fieldAmount].replace(/,/g, ''))
      : data[fieldAmount];
  return {
    amountValue,
    aprLabel,
    apyLabel,
    fieldName: name,
    fieldRates,
    gridLabel: label,
    gridValue: JSON.parse(data[fieldRates]),
    hasTerm: !!fieldTerm,
    help,
    rateTierUrl: resources?.rateTierUrl,
    termValue: fieldTerm && fields[fieldTerm].lookupValues[data[fieldTerm]],
  };
};

export default connect(mapStateToProps, {
  onChange: dataActions.changeData,
  openDialog: dialogActions.open,
})(OutputRatesStyled);
